import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { motion } from "framer-motion";
import { Receipt, Zap, Share2, BookOpen } from "lucide-react";
import GuideCards, { GUIDE_SEEN_KEY } from "@/components/GuideCards";
import PlayBadge from "@/components/PlayBadge";
import { getGuide } from "@/lib/userGuide";
import { useLang } from "@/lib/i18n";
import FaqSection from "@/components/FaqSection";
import { usePageMeta } from "@/lib/seo";
import ChatSimulator from "@/components/ChatSimulator";
import Pricing from "@/components/Pricing";
import InstallPrompt from "@/components/InstallPrompt";
import SiteFooter from "@/components/SiteFooter";
import TrustBanner from "@/components/TrustBanner";
import LanguageSelector from "@/components/LanguageSelector";
import { usePages } from "@/lib/i18nPages";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5 },
};

const BRANDS = {
  kirana: {
    name: "KashAssist for Kirana and Vegetable Vendors",
    seoTitle: "KashAssist for Kirana and Vegetable Vendors — Bill, GST & Udhaar Khata",
    short: ["KashAssist", " for Kirana & Vegetable Vendors"],
    accent: "#006D5B",
    icon: <Receipt size={18} />,
    statValues: ["3 free", "₹99", "0%–18%"],
    featureIcons: [<Receipt size={22} />, <Share2 size={22} />, <Zap size={22} />],
    img: "https://images.unsplash.com/photo-1599624581360-cb414dc85f40?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHw0fHxJbmRpYW4lMjBraXJhbmElMjBzdG9yZSUyMG93bmVyJTIwc2hvcHxlbnwwfHx8fDE3ODY4NDg3NzR8MA&ixlib=rb-4.1.0&q=85",
    planId: "kirana_pro",
  },
};

export default function ProductSite({ bot }) {
  const B = BRANDS[bot];
  const pages = usePages();
  const P = pages.kirana;
  const C = pages.common;
  const { lang } = useLang();
  const GU = getGuide(lang).ui;
  const [showCards, setShowCards] = useState(false);

  useEffect(() => {
    if (localStorage.getItem(GUIDE_SEEN_KEY)) return;
    const t = setTimeout(() => setShowCards(true), 1500);
    return () => clearTimeout(t);
  }, []);

  usePageMeta({
    title: B.seoTitle,
    description: "Bolo ya likho — GST-ready bill seconds mein, udhaar khata auto-tracked, customer ko WhatsApp pe forward. Dukaandaar ke liye AI munshi. 3 bills free, no signup.",
    canonical: "https://kashassist.com/",
  });

  useEffect(() => {
    localStorage.setItem("kash_product", "/");
    if (window.location.hash) {
      const el = document.querySelector(window.location.hash);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 300);
    }
  }, [B, bot]);

  return (
    <div className="noise-bg min-h-screen">
      <nav className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div data-testid="brand-logo" className="flex items-center gap-2">
            {bot === "kirana" ? (
              <img src="/icons/kirana-192.png" alt="KashAssist logo" className="w-9 h-9 rounded-xl shadow-sm" />
            ) : (
              <div style={{ backgroundColor: B.accent }} className="w-9 h-9 rounded-xl text-white flex items-center justify-center">
                {B.icon}
              </div>
            )}
            <span className="font-heading font-bold text-xl">
              {B.short[0]}<span style={{ color: B.accent }} className="hidden sm:inline">{B.short[1]}</span>
            </span>
          </div>
        </div>
        <div className="flex items-center gap-3 sm:gap-4">
          <LanguageSelector />
          <Link data-testid="nav-guide-link" to="/guide" style={{ color: B.accent }} aria-label={GU.nav} title={GU.nav} className="inline-flex items-center gap-1.5 text-sm font-semibold whitespace-nowrap hover:underline">
            <BookOpen size={17} /> <span className="hidden sm:inline">{GU.nav}</span>
          </Link>
          <a href="https://kashsam.ai" target="_blank" rel="noopener noreferrer" data-testid="nav-kashsam-logo" title="Kashsam Data Solutions" className="hidden sm:block">
            <img src="/kashsam-logo.webp" alt="Kashsam Data Solutions" className="h-8 w-auto opacity-60 hover:opacity-100 transition-opacity duration-150" />
          </a>
          <a data-testid="nav-pricing-link" href="#pricing" style={{ color: B.accent }} className="text-sm font-semibold whitespace-nowrap hover:underline">
            {C.pricing}
          </a>
        </div>
      </nav>

      <header className="max-w-6xl mx-auto px-6 pt-10 pb-24 grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
        <motion.div {...fadeUp} className="md:col-span-7">
          <span className="inline-flex items-center gap-1.5 bg-[#FFC107]/20 text-[#8a6d00] text-xs font-bold px-3 py-1.5 rounded-full mb-6">
            <Zap size={13} /> {P.badge}
          </span>
          <h1 data-testid="hero-heading" className="font-heading text-5xl md:text-6xl font-bold leading-[1.05] mb-6">
            {P.l1}<br /><span style={{ color: B.accent }}>{P.l2}</span><br /><span style={{ color: B.accent }}>{P.l3}</span>
          </h1>
          <p className="text-lg text-gray-600 leading-relaxed max-w-lg mb-8">{P.desc}</p>
          <div className="flex flex-wrap gap-4 items-center">
            <a
              data-testid="hero-cta-btn"
              href="#try"
              style={{ backgroundColor: B.accent }}
              className="text-white font-semibold px-8 py-4 rounded-full shadow-lg hover:-translate-y-1 hover:shadow-xl transition-transform duration-200"
            >
              {C.tryFree}
            </a>
            <PlayBadge testid="hero-play-badge" className="h-[52px]" />
            <span className="text-sm text-gray-400">{C.noSignup}</span>
          </div>
          <div className="flex gap-10 mt-12">
            {B.statValues.map((a, i) => (
              <div key={a}>
                <p style={{ color: B.accent }} className="font-heading font-bold text-2xl">{a}</p>
                <p className="text-xs text-gray-500">{P.statLabels[i]}</p>
              </div>
            ))}
          </div>
        </motion.div>
        <motion.div
          id="try"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="md:col-span-5 flex justify-center"
        >
          <ChatSimulator fixedBot={bot} onHowTo={() => setShowCards(true)} />
        </motion.div>
      </header>

      <TrustBanner />

      <section className="bg-white border-y border-black/5">
        <div className="max-w-6xl mx-auto px-6 py-24 grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <motion.div {...fadeUp} className="md:col-span-5">
            <img src={B.img} alt={B.name} loading="lazy" width="1280" height="320" className="rounded-3xl shadow-lg w-full h-80 object-cover" />
          </motion.div>
          <div className="md:col-span-7">
            <motion.h2 {...fadeUp} className="font-heading text-4xl font-bold mb-12">{P.featureTitle}</motion.h2>
            <div className="space-y-8">
              {P.features.map((f, i) => (
                <motion.div key={f.title} {...fadeUp} transition={{ duration: 0.5, delay: i * 0.1 }} className="flex gap-5" data-testid={`feature-row-${i}`}>
                  <div style={{ backgroundColor: B.accent }} className="w-12 h-12 shrink-0 rounded-2xl text-white flex items-center justify-center">
                    {B.featureIcons[i]}
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-lg mb-1">{f.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{f.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Pricing only={B.planId} accent={B.accent} />

      <FaqSection />

      <SiteFooter brand={B.name} />
      <InstallPrompt accent={B.accent} />
      <AnimatePresence>{showCards && <GuideCards onClose={() => setShowCards(false)} />}</AnimatePresence>
    </div>
  );
}
